import React, { useState, useEffect } from 'react';
import InPostPointSelector from './InPostPointSelector';
import CustomerSelector from './CustomerSelector';
import BaseModal from '../common/BaseModal';
import api from '../../services/api';

const CourierPickupModal = ({ isOpen, onClose, selectedShipments, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Typ miejsca odbioru: 'address' (kurier przyjedzie pod adres) lub 'point' (nadanie w paczkomacie)
  const [pickupType, setPickupType] = useState('address');
  const [selectedPoint, setSelectedPoint] = useState(null);

  // Miejsce odbioru (wybrane z listy klientów/lokalizacji)
  const [pickupLocation, setPickupLocation] = useState(null);
  const [comments, setComments] = useState('');

  // Reset stanu przy otwarciu
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    // [L-dblsubmit] Nie pozwól na podwójne wysłanie: w trakcie requestu (loading)
    // ani po sukcesie (success ustawiony na czas 2s przed zamknięciem modala).
    if (loading || success) {
      return;
    }

    if (!selectedShipments || selectedShipments.length === 0) {
      setError('Brak wybranych przesyłek');
      return;
    }

    // Walidacja dla typu 'point'
    if (pickupType === 'point' && !selectedPoint) {
      setError('Wybierz punkt nadania');
      return;
    }

    // Walidacja dla typu 'address'
    if (pickupType === 'address' && !pickupLocation) {
      setError('Wybierz miejsce odbioru');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const payload = {
        shipment_ids: selectedShipments.map(s => s.inpost_id),
        comments: comments,
        pickup_type: pickupType
      };

      if (pickupType === 'point') {
        // Nadanie w paczkomacie/POP
        payload.dispatch_point = selectedPoint.name;
      } else {
        // Odbiór kurierem pod adres - dane z wybranej lokalizacji (klienta)
        payload.name = pickupLocation.nazwa_firmy || `${pickupLocation.imie || ''} ${pickupLocation.nazwisko || ''}`.trim();
        payload.phone = pickupLocation.telefon || '';
        payload.email = pickupLocation.email || '';
        payload.street = pickupLocation.ulica || pickupLocation.adres || '';
        payload.city = pickupLocation.miasto || '';
        payload.post_code = pickupLocation.kod_pocztowy || '';
      }

      const response = await api.post('/shipping/dispatch-orders', payload);

      const result = response.data;

      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Błąd tworzenia zlecenia odbioru');
      }
    } catch (e) {
      // [L-pickuperr] Pokaż rzeczywisty komunikat błędu (np. 4xx z walidacją),
      // a nie zawsze "Błąd połączenia z serwerem".
      setError(e?.message || 'Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    backgroundColor: 'var(--card-bg)',
    color: 'var(--text)',
    outline: 'none'
  };

  const buttonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500'
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="🚚 Zamów odbiór kuriera"
      size="md"
      zIndex={1050}
      footer={
        <>
          <button
            onClick={onClose}
            style={{ ...buttonStyle, border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !!success || !selectedShipments?.length || (pickupType === 'point' && !selectedPoint) || (pickupType === 'address' && !pickupLocation)}
            style={{
              ...buttonStyle,
              border: `1px solid ${(loading || success) ? 'var(--text-muted)' : 'var(--success)'}`,
              color: (loading || success) ? 'var(--text-muted)' : 'var(--success)'
            }}
          >
            {loading ? '⏳ Wysyłanie...' : (success ? '✅ Wysłano' : (pickupType === 'point' ? '📦 Potwierdź nadanie' : '🚚 Zamów odbiór'))}
          </button>
        </>
      }
    >
      {/* Komunikaty */}
      {error && (
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', marginBottom: '1rem', fontSize: '11px' }}>
          ❌ {error}
        </div>
      )}
      {success && (
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--success-light)', color: 'var(--success)', borderRadius: '4px', marginBottom: '1rem', fontSize: '11px' }}>
          ✅ {success}
        </div>
      )}

      {/* Info o wybranych przesyłkach */}
      <div style={{
        backgroundColor: 'var(--info-light)',
        border: '1px solid var(--info-light)',
        borderRadius: '8px',
        padding: '0.75rem',
        marginBottom: '1rem'
      }}>
        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '0.5rem' }}>
          📦 Wybrane przesyłki: {selectedShipments?.length || 0}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--info)', maxHeight: '80px', overflowY: 'auto' }}>
          {selectedShipments?.map((s, i) => (
            <div key={i}>
              • {s.tracking_number || s.inpost_id} - {s.receiver_name}
            </div>
          ))}
        </div>
      </div>

      {/* Wybór typu odbioru */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
          📍 Skąd kurier ma odebrać przesyłki?
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div
            onClick={() => setPickupType('address')}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: pickupType === 'address' ? '2px solid var(--info)' : '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: pickupType === 'address' ? 'var(--info-light)' : 'var(--card-bg)' ,
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>🏠</div>
            <div style={{ fontSize: '11px', fontWeight: '600' }}>Adres</div>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kurier przyjedzie pod wskazany adres</div>
          </div>
          <div
            onClick={() => setPickupType('point')}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: pickupType === 'point' ? '2px solid var(--info)' : '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: pickupType === 'point' ? 'var(--info-light)' : 'var(--card-bg)' ,
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>📦</div>
            <div style={{ fontSize: '11px', fontWeight: '600' }}>Paczkomat / POP</div>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Nadam w wybranym punkcie</div>
          </div>
        </div>
      </div>

      {/* Jeśli paczkomat - wybór punktu */}
      {pickupType === 'point' && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
            Wybierz punkt nadania:
          </div>
          <InPostPointSelector
            selectedPoint={selectedPoint}
            onSelect={setSelectedPoint}
            onClear={() => setSelectedPoint(null)}
            placeholder="Wpisz miasto lub kod pocztowy..."
          />
        </div>
      )}

      {/* Miejsce odbioru - tylko dla typu 'address' */}
      {pickupType === 'address' && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
            Wybierz miejsce odbioru (dodaj jako "klienta" np. "Magazyn", "Biuro"):
          </div>
          <CustomerSelector
            selectedCustomer={pickupLocation}
            onSelect={setPickupLocation}
            onClear={() => setPickupLocation(null)}
            showHistory={false}
          />
        </div>
      )}

      {/* Komentarz */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>
          Komentarz dla kuriera (opcjonalnie)
        </label>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="np. Dzwonek nie działa, proszę zadzwonić..."
          style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
        />
      </div>

      {/* Info */}
      <div style={{
        backgroundColor: pickupType === 'point' ? 'var(--success-light)' : 'var(--warning-light)',
        border: `1px solid ${pickupType === 'point' ? 'var(--success-light)' : 'var(--warning-light)'}`,
        borderRadius: '8px',
        padding: '0.75rem',
        fontSize: '11px',
        color: pickupType === 'point' ? 'var(--success)' : 'var(--warning)'
      }}>
        {pickupType === 'point' ? (
          <>
            📦 <strong>Nadanie w punkcie:</strong> Wydrukuj etykiety i zanieś paczki do wybranego paczkomatu lub punktu POP.
            Możesz to zrobić w dowolnym momencie.
          </>
        ) : (
          <>
            ⚠️ <strong>Ważne:</strong> Kurier odbierze przesyłki w ciągu najbliższego dnia roboczego.
            Upewnij się, że wszystkie przesyłki mają naklejone etykiety.
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default CourierPickupModal;
